// SpeechRecognition is fully supported in Chrome/Edge (desktop + Android).
// Safari 17.4+ supports it behind the webkit prefix.
// Firefox does NOT support SpeechRecognition as of 2024.
// SpeechSynthesis is supported in all modern browsers.
// Show a graceful banner for unsupported browsers instead of crashing.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Mic, MicOff, Play, StopCircle, History, Sparkles, Volume2, LogOut, Info } from 'lucide-react';
import InterviewerAvatar from './InterviewerAvatar';
import TopMenu from '../../components/TopMenu';
import useAIInterview from './useAIInterview';
import { parseFeedback, feedbackToPlainText } from './parseFeedback';
import '../../components/MockInterview/MockInterview.css';
import './AIInterview.css';

const TOUR_SEEN_KEY = 'ai-interview-tour-seen';

/* ── Session timer shown in the video area ── */
const VcTimer = ({ active }) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!active) { setSecs(0); return; }
    const t = setInterval(() => setSecs(s => s + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');
  return <div className="vc-timer">{mm}:{ss}</div>;
};


const STATUS_LABEL = {
  idle:      'Your turn',
  listening: 'Listening…',
  loading:   'Thinking…',
  speaking:  'Speaking…',
};

const ABOUT_MAX = 400;

const scoreColor = (s) => {
  if (s == null) return '#9ca3af';
  if (s >= 8) return '#10b981';
  if (s >= 5) return '#facc15';
  return '#ef4444';
};

const AIInterviewPage = ({ theme = 'dark', toggleTheme }) => {
  const navigate = useNavigate();
  const dark = theme === 'dark';
  const {
    phase,
    formData, setFormData,
    history,
    status,
    isAllowed,
    notAllowedReason,
    checking,
    score,
    feedback,
    speechSupported,
    checkAllowed,
    startSession,
    endSession,
    resetInterview,
    speakText,
    startListening,
    stopListening,
  } = useAIInterview();

  const transcriptEndRef = useRef(null);
  const [showTour, setShowTour] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);

  useEffect(() => { checkAllowed(); }, [checkAllowed]);

  // First-time tour: show once, when the user enters the live phase.
  useEffect(() => {
    if (phase !== 'live') return;
    try {
      if (!localStorage.getItem(TOUR_SEEN_KEY)) setShowTour(true);
    } catch { /* localStorage unavailable — silently skip */ }
  }, [phase]);

  const dismissTour = () => {
    try { localStorage.setItem(TOUR_SEEN_KEY, '1'); } catch {}
    setShowTour(false);
  };

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [history, status]);

  const speakDisabled = status === 'loading' || status === 'speaking' || !speechSupported;

  // ── Phase 3: Evaluating / Results ────────────────────────────────
  if (phase === 'evaluating') {
    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="ai-eval-stage">
          <div className="ai-eval-orb">
            <Sparkles size={48} />
          </div>
          <div>
            <h2 className="ai-eval-title">Evaluating your interview</h2>
            <p className="ai-eval-sub">
              Hang tight while we analyse your responses and generate detailed feedback.
            </p>
          </div>
          <div className="ai-eval-steps">
            <div className="ai-eval-step ai-eval-step-done">
              <span className="ai-eval-step-icon" />
              <span>Recorded transcript captured</span>
            </div>
            <div className="ai-eval-step ai-eval-step-active">
              <span className="ai-eval-step-icon" />
              <span>Scoring answers and skills</span>
            </div>
            <div className="ai-eval-step">
              <span className="ai-eval-step-icon" />
              <span>Composing personalised feedback</span>
            </div>
          </div>
          <div className="ai-eval-bar"><div className="ai-eval-bar-fill" /></div>
        </div>
      </div>
    );
  }

  if (phase === 'results') {
    const pct   = score != null ? Math.max(0, Math.min(100, (score / 10) * 100)) : 0;
    const color = scoreColor(score);

    // feedback is always a parsed object from useAIInterview { summary, strengths, weaknesses, improvements }
    const fb = parseFeedback(feedback);
    const strengths    = fb.strengths    ?? [];
    const weaknesses   = fb.weaknesses   ?? [];
    const improvements = fb.improvements ?? [];
    const plainText    = fb.summary      ?? '';

    const hasStructured = strengths.length || weaknesses.length || improvements.length;

    return (
      <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
        <TopMenu theme={theme} toggleTheme={toggleTheme} />
        <div className="mi-container" style={{ maxWidth: 760 }}>

          {/* Score header */}
          <div className="ai-result-hero">
            <div className="ai-result-score-ring" style={{ '--score-color': color }}>
              <span className="ai-result-score-num">
                {score != null ? score.toFixed(1) : '—'}
              </span>
              <span className="ai-result-score-denom">/ 10</span>
            </div>
            <div>
              <h1 className="mi-title" style={{ fontSize: '1.9rem', marginBottom: 6 }}>
                Interview Complete
              </h1>
              <p style={{ opacity: 0.6, margin: 0 }}>
                {score >= 8 ? 'Outstanding performance — well done!' :
                 score >= 6 ? 'Good interview — solid answers with room to improve.' :
                 score >= 4 ? 'Decent start — keep practising.' :
                 'Needs improvement — review your answers below.'}
              </p>
            </div>
          </div>

          {/* Score bar */}
          <div className="mi-card" style={{ padding: '18px 20px' }}>
            <div className="mi-row mi-row-between" style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, opacity: 0.7 }}>Overall Score</span>
              <strong style={{ fontSize: 20, color }}>{score != null ? `${score.toFixed(1)} / 10` : '—'}</strong>
            </div>
            <div className="mi-progress-track">
              <div className="mi-progress-fill"
                style={{ width: `${pct}%`, background: color, transition: 'width 1s ease' }} />
            </div>
          </div>

          {/* Structured feedback sections */}
          {hasStructured ? (
            <div className="ai-result-grid">
              {strengths.length > 0 && (
                <div className="ai-result-section ai-result-strength">
                  <h4>✅ Strengths</h4>
                  <ul>{strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                </div>
              )}
              {weaknesses.length > 0 && (
                <div className="ai-result-section ai-result-weakness">
                  <h4>⚠️ Areas to Improve</h4>
                  <ul>{weaknesses.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
              {improvements.length > 0 && (
                <div className="ai-result-section ai-result-improve" style={{ gridColumn: '1 / -1' }}>
                  <h4>💡 Suggestions</h4>
                  <ul>{improvements.map((imp, i) => <li key={i}>{imp}</li>)}</ul>
                </div>
              )}
              {plainText && (
                <div className="mi-card" style={{ gridColumn: '1 / -1' }}>
                  <p className="mi-kicker" style={{ margin: '0 0 6px' }}>Summary</p>
                  <p style={{ lineHeight: 1.6, opacity: 0.85, whiteSpace: 'pre-wrap', margin: 0 }}>
                    {plainText}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="mi-card">
              <p className="mi-kicker" style={{ margin: '0 0 8px' }}>Feedback</p>
              <p style={{ lineHeight: 1.7, opacity: 0.88, whiteSpace: 'pre-wrap', margin: 0 }}>
                {plainText || feedbackToPlainText(fb) || 'No feedback returned for this session.'}
              </p>
            </div>
          )}

          <div className="mi-row mi-row-end" style={{ marginTop: 22, gap: 10 }}>
            <button className="mi-btn" onClick={() => navigate('/ai-interview/history')}>
              <History size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              View History
            </button>
            <button className="mi-btn mi-btn-primary" onClick={resetInterview}>
              Start New Interview
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Phase 2: Live interview — video-conference layout ───────────
  if (phase === 'live') {
    // Last AI question to display at bottom
    const lastQuestion = [...history].reverse().find(h => h.role === 'model')?.text
      ?.replace(/\[END\]/gi, '').trim() || '';

    // Stage label based on how many user turns
    const turnCount = history.filter(h => h.role === 'user').length;
    const stageName = turnCount === 0 ? 'Warm Up'
      : turnCount < 3 ? 'Introduction'
      : 'Technical Round';

    return (
      <div className="vc-shell">

        {/* ── Tour overlay ───────────────────────────────── */}
        {showTour && (
          <div className="mi-tour-backdrop" role="dialog" aria-modal="true">
            <div className="mi-tour-card">
              <h3>How the AI interview works</h3>
              <p>Voice-only — speak naturally, the AI listens and responds.</p>
              <ol>
                <li>Press <kbd>Start Answer</kbd> to begin recording.</li>
                <li>Speak at a comfortable pace — no time limit.</li>
                <li>Press <kbd>Stop Answer</kbd> when you are done.</li>
                <li>The AI will process your answer and ask the next question.</li>
              </ol>
              <div className="mi-tour-actions">
                <button className="mi-btn" onClick={dismissTour}>Skip</button>
                <button className="mi-btn mi-btn-primary" onClick={dismissTour}>Got it!</button>
              </div>
            </div>
          </div>
        )}

        {/* ── End confirm modal ──────────────────────────── */}
        {showEndConfirm && (
          <div className="mi-modal-backdrop" onClick={() => setShowEndConfirm(false)}>
            <div className="mi-modal ai-end-modal" onClick={(e) => e.stopPropagation()}>
              <div className="mi-modal-head">
                <h3>End interview early?</h3>
                <button className="mi-modal-close" onClick={() => setShowEndConfirm(false)}>×</button>
              </div>
              <p>You'll receive a full AI evaluation based on your answers so far.</p>
              <div className="mi-row mi-row-end" style={{ gap: 10 }}>
                <button className="mi-btn" onClick={() => setShowEndConfirm(false)}>Continue</button>
                <button className="mi-btn mi-btn-danger" onClick={() => { setShowEndConfirm(false); endSession(); }}>
                  <StopCircle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  End &amp; Evaluate
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════════
            BODY — main video + right sidebar
            ══════════════════════════════════════════════════ */}
        <div className="vc-body">

          {/* ── MAIN VIDEO ────────────────────────────────── */}
          <div className="vc-main">
            <div className="vc-bg" />

            {/* Avatar */}
            <div className="vc-avatar-wrap">
              <InterviewerAvatar status={status} />
            </div>

            {/* User speaking waveform (visible while listening) */}
            {status === 'listening' && (
              <div className="vc-user-wave">
                {[0,1,2,3,4,5,6].map(i => (
                  <div key={i} className="vc-user-wave-bar"
                    style={{ animationDelay: `${i * 0.09}s` }} />
                ))}
              </div>
            )}

            {/* Status badge — sits just above the controls */}
            <div className={`vc-status-badge vc-status-${status}`}
              style={{ opacity: status === 'idle' ? 0 : 1 }}>
              {status === 'listening' && <span className="vc-rec-dot" />}
              {STATUS_LABEL[status]}
            </div>

            {/* Timer — top left */}
            <VcTimer active={phase === 'live'} />

            {/* Start / Stop button */}
            <div className="vc-controls">
              <button
                className={`vc-speak-btn ${status === 'listening' ? 'vc-speak-btn-stop' : ''}`}
                onClick={status === 'listening' ? stopListening : startListening}
                disabled={speakDisabled}
              >
                {status === 'listening'
                  ? <><MicOff size={18} /> Stop Answer</>
                  : <><Mic size={18} /> Start Answer</>}
              </button>
            </div>
          </div>

          {/* ── RIGHT SIDEBAR ─────────────────────────────── */}
          <aside className="vc-sidebar">
            <div className="vc-info-card">

              {/* AI avatar icon */}
              <div className="vc-info-icon">
                <Bot size={26} />
              </div>

              <h3 className="vc-job-title">{formData.jobRole || 'Interview'}</h3>
              <p className="vc-stage">{stageName}</p>

              {/* Live status chip */}
              <div className="vc-live-status" data-status={status !== 'idle' ? status : ''}>
                <span className="vc-live-dot" />
                {STATUS_LABEL[status]}
              </div>

              <div className="vc-sidebar-divider" />

              <div className="vc-sidebar-stat">
                <span className="vc-stat-label">Questions answered</span>
                <span className="vc-stat-value">{turnCount}</span>
              </div>

              <div className="vc-sidebar-stat">
                <span className="vc-stat-label">Round</span>
                <span className="vc-stat-label" style={{ color: '#a5b4fc', fontWeight: 700 }}>
                  {stageName}
                </span>
              </div>

              <div className="vc-sidebar-actions">
                <button className="vc-action-btn vc-action-info" onClick={() => setShowTour(true)}>
                  <Info size={14} />
                  How It's Evaluated
                </button>
                <button className="vc-action-btn vc-action-exit" onClick={() => setShowEndConfirm(true)}>
                  <LogOut size={14} />
                  End Interview
                </button>
              </div>
            </div>
          </aside>
        </div>

        {/* ── QUESTION BAR ──────────────────────────────────── */}
        <div className="vc-question-bar">
          <div className="vc-question-text">
            {status === 'loading' ? (
              <span className="vc-q-loading">
                <span /><span /><span />
              </span>
            ) : lastQuestion ? (
              <>
                <span className="vc-q-label">
                  {turnCount === 0 ? 'Opening Question' : `Question ${turnCount}`}
                </span>
                <span className="vc-q-body">{lastQuestion}</span>
              </>
            ) : (
              <span className="vc-q-body" style={{ opacity: 0.4 }}>
                The interviewer will begin shortly…
              </span>
            )}
          </div>
          {lastQuestion && status !== 'loading' && (
            <button
              className="vc-repeat-btn"
              onClick={() => speakText(lastQuestion)}
              disabled={status === 'speaking' || status === 'listening'}
            >
              <Volume2 size={14} /> Repeat
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Phase 1: Intro form ──────────────────────────────────────────
  return (
    <div className={`mi-page ${dark ? '' : 'mi-light'}`}>
      <TopMenu theme={theme} toggleTheme={toggleTheme} />
      <div className="mi-ed" style={{ maxWidth: 820 }}>
        <div className="mi-ed-strip">
          <div className="mi-ed-strip-l">
            <span className="mi-ed-issue">№ 02 — AI Mock Interview</span>
            <span>Voice only</span>
            <span>Chrome / Edge</span>
          </div>
          <button className="mi-ed-back" onClick={() => navigate('/mock-interview')}>
            <ArrowLeft size={12} />
            Studio
          </button>
        </div>

        <div className="mi-ed-pagehead">
          <h1>
            Tell us your <em>story</em>.
            <svg
              className="mi-ed-scribble"
              viewBox="0 0 220 12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M3 8 C 50 2, 110 11, 165 5 S 210 9, 217 4" />
            </svg>
          </h1>
          <p>
            Three quick fields. The AI will spin up a tailored round around
            your stack — speak your answers, it speaks back, and at the end
            you get a scored breakdown.
          </p>
        </div>

        {!speechSupported && (
          <div className="mi-notice" style={{ marginBottom: 18 }}>
            <h4>Voice input not supported here</h4>
            <p>Open this page in Chrome or Edge — Safari and Firefox don&rsquo;t support voice input yet.</p>
          </div>
        )}

        {!checking && !isAllowed && (
          <div className="mi-notice" style={{ marginBottom: 18 }}>
            <h4>You&rsquo;re out of free minutes.</h4>
            <p>{notAllowedReason || 'Upgrade your plan to keep practicing without limits.'}</p>
            <button className="mi-ed-go" onClick={() => navigate('/settings')}>
              See plans
            </button>
          </div>
        )}

        <div className="mi-ed-panel">
          <div className="mi-ed-step">
            <span className="mi-ed-step-num">01</span>
            <span className="mi-ed-step-title">Who are you, professionally?</span>
            <span className="mi-ed-step-hint">— takes 10 seconds</span>
          </div>
          <div className="mi-ed-row-2">
            <div>
              <label className="mi-ed-label">Job role</label>
              <input
                className="mi-ed-input"
                value={formData.jobRole}
                onChange={(e) => setFormData((f) => ({ ...f, jobRole: e.target.value }))}
                placeholder="Frontend Developer"
              />
            </div>
            <div>
              <label className="mi-ed-label">Years of experience</label>
              <input
                className="mi-ed-input"
                value={formData.yearsOfExperience}
                onChange={(e) => setFormData((f) => ({ ...f, yearsOfExperience: e.target.value }))}
                placeholder="2"
              />
            </div>
          </div>

          <div className="mi-ed-step">
            <span className="mi-ed-step-num">02</span>
            <span className="mi-ed-step-title">What do you actually use?</span>
            <span className="mi-ed-step-hint">— questions match your stack</span>
          </div>
          <label className="mi-ed-label">Tech stack</label>
          <input
            className="mi-ed-input"
            value={formData.techStack}
            onChange={(e) => setFormData((f) => ({ ...f, techStack: e.target.value }))}
            placeholder="React, TypeScript, Node.js"
          />

          <div className="mi-ed-step">
            <span className="mi-ed-step-num">03</span>
            <span className="mi-ed-step-title">A few lines about you.</span>
            <span className="mi-ed-step-hint">— sets the tone</span>
          </div>
          <label className="mi-ed-label">
            About yourself <span style={{ opacity: 0.6, fontWeight: 600, letterSpacing: 0 }}>· {formData.about.length}/{ABOUT_MAX}</span>
          </label>
          <textarea
            className="mi-ed-textarea"
            value={formData.about}
            onChange={(e) =>
              setFormData((f) => ({ ...f, about: e.target.value.slice(0, ABOUT_MAX) }))
            }
            placeholder="Background, recent projects, what you want to grow into."
          />
          <p className="mi-ed-help">
            The AI uses this to pick relevant follow-ups. Keep it real —
            no need to polish.
          </p>

          <div className="mi-row mi-row-between" style={{ marginTop: 22, gap: 10, flexWrap: 'wrap' }}>
            <button className="mi-btn" onClick={() => navigate('/ai-interview/history')}>
              <History size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Past sessions
            </button>
            <button
              className="mi-ed-go"
              onClick={startSession}
              disabled={!isAllowed || !speechSupported || checking}
            >
              <Play size={16} />
              Start the round
            </button>
          </div>
        </div>

        <p className="mi-ed-margin" style={{ marginTop: 22 }}>
          First time? Press <b>Start Answer</b>, talk like you would in a
          real call, then <b>Stop Answer</b>. The AI does the rest.
        </p>
      </div>
    </div>
  );
};

export default AIInterviewPage;
